import React, { useEffect, useRef, useState } from "react"
import { Image, Modal, Spin } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf"

// Set the PDF.js worker source to the file hosted in your public folder.
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js"

const InvoicePDFPreview = ({ pdfUrl, scale = 1.5, width = 700 }) => {
  const canvasRef = useRef(null)
  const [imgSrc, setImgSrc] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Ensure canvasRef is current before proceeding
    const canvas = canvasRef.current
    if (!canvas) {
      console.warn("Canvas ref not available yet.")
      // Decide if you want to retry or just exit
      setIsLoading(false) // If no canvas, stop loading
      return
    }
    const renderPDFPage = async () => {
      setIsLoading(true)
      setError(null)
      setImgSrc(null)
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        // Render the first page of the PDF
        const page = await pdf.getPage(1)
        const viewport = page.getViewport({ scale })
        const canvas = canvasRef.current
        const context = canvas.getContext("2d")
        canvas.width = viewport.width
        canvas.height = viewport.height

        const renderContext = { canvasContext: context, viewport }
        await page.render(renderContext).promise

        // Convert canvas content to a JPEG data URL
        const dataURL = canvas.toDataURL("image/jpeg")
        setImgSrc(dataURL)
      } catch (error) {
        console.error("Error rendering PDF:", error)
      } finally {
        setIsLoading(false) // <<< THIS WAS MISSING! Add it here.
      }
    }

    if (pdfUrl) {
      // Only attempt to render if a URL is provided
      renderPDFPage()
    } else {
      // Handle the case where there's no pdfUrl initially or it's cleared
      setIsLoading(false)
      setImgSrc(null)
      setError(null)
    }
  }, [pdfUrl, scale])

  return (
    <>
      {/* Hidden canvas used to render the PDF */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* --- Conditional Rendering --- */}
      {isLoading ? (
        <Spin size="small" /> // <-- Show spinner while loading
      ) : imgSrc ? (
        // Show icon when the image is ready; clicking it opens the modal
        <div onClick={() => setPreviewVisible(true)} style={{ cursor: "pointer" }}>
          <EyeOutlined style={{ color: "blue" }} />
        </div>
      ) : error ? (
        // Optional: Show an indication of error (e.g., a disabled/red icon)
        <EyeOutlined style={{ color: "red", cursor: "not-allowed" }} title={`Error: ${error}`} />
      ) : // Optional: Render nothing or a placeholder if there's no URL and not loading
      null}

      {/* Modal with the full-screen preview - only allow opening if imgSrc exists */}
      <Modal
        open={previewVisible && imgSrc} // <-- Only allow opening if we have an image
        footer={null}
        width={width}
        onCancel={() => setPreviewVisible(false)}
        centered
        // Ensure modal content doesn't try to render if imgSrc is null
      >
        {imgSrc && <Image src={imgSrc} alt="PDF preview" preview={false} />}
      </Modal>
    </>
  )
}

export default InvoicePDFPreview
